/**
 * hk_os · <TaskCompletionButton>
 * ────────────────────────────────────────────────────────────────────
 * Bottom-of-screen primary CTA. 56 px min-height per ADR-014 P1. Label
 * comes from a voice snippet key so it stays localised without prop
 * drilling.
 *
 * Props:
 *   voiceKey     — snippet key that also provides the button label
 *   labelOverride— optional plain label (rare)
 *   icon         — lucide icon component
 *   tone         — "primary" | "success" | "danger"
 *   onClick
 *   loading, disabled, fullWidth (default true)
 *   testid
 */
import React from "react";
import { Loader2 } from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

const TONES = {
  primary: "bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white border-sky-600 focus-visible:ring-sky-300",
  success: "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white border-emerald-600 focus-visible:ring-emerald-300",
  danger:  "bg-red-600 hover:bg-red-700 active:bg-red-800 text-white border-red-600 focus-visible:ring-red-300",
};

export function TaskCompletionButton({
  voiceKey,
  labelOverride,
  icon: Icon,
  tone = "success",
  onClick,
  loading,
  disabled,
  fullWidth = true,
  testid,
}) {
  const { getSnippet } = useVoiceRegistry();
  const snippet = voiceKey ? getSnippet(voiceKey) : null;
  const label = labelOverride || snippet?.text || "Complete";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testid || `task-complete-btn-${voiceKey || "custom"}`}
      lang={snippet?.locale || undefined}
      className={[
        "inline-flex items-center justify-center gap-3",
        "min-h-[56px] rounded-2xl border-2 px-8",
        "text-[20px] font-bold leading-tight tracking-wide",
        "transition-all active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-4",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        TONES[tone] || TONES.primary,
        fullWidth ? "w-full" : "",
      ].join(" ")}
    >
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin" strokeWidth={2.4} />
      ) : (
        Icon && <Icon className="w-6 h-6" strokeWidth={2.4} />
      )}
      <span>{label}</span>
    </button>
  );
}

export default TaskCompletionButton;
